import { jwtDecode } from 'jwt-decode';
import { AuthToken } from './token.model';
import { LoggedUserDtoRolesEnum } from '@api/client';
import ReactGA from 'react-ga4';
import { StorageKeysEnum } from 'enums/storageKeys.enum';
import { HOSTNAME_PRODUCTION, HOSTNAME_WEB_SVIL, URL_MICROSOFT_LOGIN, URL_MICROSOFT_LOGIN_SVIL } from 'env';
import { PathEnum } from 'core/router/path.enum';
interface UserId {
  USERID: number;
}
namespace AuthService {
  export const getToken = () => {
    let token = localStorage.getItem(StorageKeysEnum.TOKEN);

    return token ? JSON.parse(token) : '';
  };

  export const getUser = () => {
    let user = localStorage.getItem(StorageKeysEnum.USER);

    return user ? JSON.parse(user) : '';
  };

  export const getUserInfoData = () => {
    const data = localStorage.getItem(StorageKeysEnum.USER_INFO);
    return data ? JSON.parse(data) : null;
  };

  const checkAuthentication = () => {
    let token = getToken();
    if (token) return true;

    return false;
  };

  export const isAuthenticated = () => checkAuthentication();

  export const setAuthentication = (token: string | undefined) => {
    if (token) localStorage.setItem(StorageKeysEnum.TOKEN, JSON.stringify(token));
  };

  export const isImpersonating = (): boolean => {
    const tokenData = decodeToken();
    return !!tokenData?.IMPERSONATION;
  };

  export const getImpersonationInfo = (): { impersonatorUsername?: string; reason?: string } | undefined => {
    const tokenData = decodeToken();
    if (!tokenData?.IMPERSONATION) return undefined;

    return {
      impersonatorUsername: tokenData.IMPERSONATOR_USERNAME,
      reason: tokenData.IMPERSONATION_REASON,
    };
  };

  export const startImpersonation = (token: string): void => {
    const currentToken = localStorage.getItem(StorageKeysEnum.TOKEN);
    const currentUser = localStorage.getItem(StorageKeysEnum.USER);
    const currentUserInfo = localStorage.getItem(StorageKeysEnum.USER_INFO);

    if (currentToken) localStorage.setItem(StorageKeysEnum.IMPERSONATOR_TOKEN, currentToken);
    if (currentUser) localStorage.setItem(StorageKeysEnum.IMPERSONATOR_USER, currentUser);
    if (currentUserInfo) localStorage.setItem(StorageKeysEnum.IMPERSONATOR_USER_INFO, currentUserInfo);

    setAuthentication(token);
    setUser();

    // Settings è riservato a SuperAdmin: si esce sempre sulla dashboard,
    // altrimenti il target impersonato ricaricherebbe su una pagina a cui non ha accesso.
    window.location.hash = `/${PathEnum.PRIVATE}/${PathEnum.DASHBOARD}`;
    window.location.reload();
  };

  export const stopImpersonation = (): void => {
    const impersonatorToken = localStorage.getItem(StorageKeysEnum.IMPERSONATOR_TOKEN);
    const impersonatorUser = localStorage.getItem(StorageKeysEnum.IMPERSONATOR_USER);
    const impersonatorUserInfo = localStorage.getItem(StorageKeysEnum.IMPERSONATOR_USER_INFO);

    if (impersonatorToken) localStorage.setItem(StorageKeysEnum.TOKEN, impersonatorToken);
    if (impersonatorUser) localStorage.setItem(StorageKeysEnum.USER, impersonatorUser);
    if (impersonatorUserInfo) localStorage.setItem(StorageKeysEnum.USER_INFO, impersonatorUserInfo);

    localStorage.removeItem(StorageKeysEnum.IMPERSONATOR_TOKEN);
    localStorage.removeItem(StorageKeysEnum.IMPERSONATOR_USER);
    localStorage.removeItem(StorageKeysEnum.IMPERSONATOR_USER_INFO);

    window.location.reload();
  };

  export const logout = (returnToLogin?: boolean) => {
    localStorage.removeItem(StorageKeysEnum.TOKEN);
    localStorage.removeItem(StorageKeysEnum.USER);
    localStorage.removeItem(StorageKeysEnum.USER_INFO);
    localStorage.removeItem(StorageKeysEnum.IS_HCC);
    sessionStorage.clear();
    if (window.location.hostname !== HOSTNAME_PRODUCTION && window.location.hostname !== HOSTNAME_WEB_SVIL) {
      window.location.reload();
    } else {
      ReactGA.set({ userId: undefined });
      const logout = returnToLogin ? '' : '/logout';
      const urlLogin =
        window.location.hostname !== HOSTNAME_PRODUCTION ? URL_MICROSOFT_LOGIN_SVIL : URL_MICROSOFT_LOGIN;
      window.location.href = `${urlLogin}${logout}`;
    }
  };

  const decodeToken = (): (AuthToken & UserId) | undefined => {
    const token = getToken();

    return token ? jwtDecode(token) : undefined;
  };

  export const setUser = () => {
    const tokenData = decodeToken();
    const user = {
      role: tokenData?.ROLE,
      name: tokenData?.NAME,
      email: tokenData?.EMAIL,
      language: tokenData?.LANGUAGE,
      userId: tokenData?.USERID,
    };
    if (window.location.hostname !== HOSTNAME_PRODUCTION) {
      ReactGA.set({ userId: Number(tokenData?.USERID) });
    }

    localStorage.setItem(StorageKeysEnum.USER, JSON.stringify(user));
  };

  export const canViewPlPage = () => {
    const user = getUser();
    return user
      ? (user.role.includes(LoggedUserDtoRolesEnum.Pl) && !user.role.includes(LoggedUserDtoRolesEnum.Am)) ||
          user.role.includes(LoggedUserDtoRolesEnum.SuperAdmin)
      : false;
  };

  export const isSuperAdmin = () => {
    const user = getUser();
    return user ? user.role.includes(LoggedUserDtoRolesEnum.SuperAdmin) : false;
  };

  export const isPl = () => {
    const user = getUser();
    return user
      ? user.role.includes(LoggedUserDtoRolesEnum.Pl) || user.role.includes(LoggedUserDtoRolesEnum.SuperAdmin)
      : false;
  };

  export const isAm = () => {
    const user = getUser();
    return user
      ? user.role.includes(LoggedUserDtoRolesEnum.Am) ||
          user.role.includes(LoggedUserDtoRolesEnum.Cl) ||
          user.role.includes(LoggedUserDtoRolesEnum.Il) ||
          user.role.includes(LoggedUserDtoRolesEnum.SuperAdmin)
      : false;
  };

  export const isCl = () => {
    const user = getUser();
    return user
      ? user.role.includes(LoggedUserDtoRolesEnum.Cl) || user.role.includes(LoggedUserDtoRolesEnum.SuperAdmin)
      : false;
  };

  export const isIl = () => {
    const user = getUser();
    return user
      ? user.role.includes(LoggedUserDtoRolesEnum.Il) || user.role.includes(LoggedUserDtoRolesEnum.SuperAdmin)
      : false;
  };

  export const isDm = () => {
    const user = getUser();
    return user
      ? user.role.includes(LoggedUserDtoRolesEnum.Dm) || user.role.includes(LoggedUserDtoRolesEnum.SuperAdmin)
      : false;
  };

  export const isWFM = () => {
    const user = getUser();
    return user
      ? user.role.includes(LoggedUserDtoRolesEnum.Wfm) || user.role.includes(LoggedUserDtoRolesEnum.SuperAdmin)
      : false;
  };

  export const isCgd = () => {
    const user = getUser();
    return user
      ? user.role.includes(LoggedUserDtoRolesEnum.Cdg) || user.role.includes(LoggedUserDtoRolesEnum.SuperAdmin)
      : false;
  };

  export const isCp = () => {
    const user = getUser();
    return user
      ? user.role.includes(LoggedUserDtoRolesEnum.Cp) || user.role.includes(LoggedUserDtoRolesEnum.SuperAdmin)
      : false;
  };

  export const isCa = () => {
    const user = getUser();
    return user
      ? user.role.includes(LoggedUserDtoRolesEnum.Ca) || user.role.includes(LoggedUserDtoRolesEnum.SuperAdmin)
      : false;
  };

  export const isHCC = () => {
    const user = getUser();
    return (
      localStorage.getItem(StorageKeysEnum.IS_HCC) === 'true' ||
      user?.role?.includes(LoggedUserDtoRolesEnum.Dm) ||
      user?.role?.includes(LoggedUserDtoRolesEnum.SuperAdmin)
    );
  };
}
export default AuthService;
